import { useState, useCallback, useEffect } from 'react';

function getCsrfToken(): string {
	return (document.cookie.match(/(?:^|; )_eid_csrf_v1=([^;]+)/)?.[1]) || '';
}

export type RequestFlowState =
	| 'idle'
	| 'composing'
	| 'previewing'
	| 'submitting'
	| 'submitted'
	| 'sod_conflict'
	| 'auth_error'
	| 'failed';

export interface ApprovalPreview {
	approval_chain: Array<{
		step_number: number;
		approver_name: string;
		approver_arn?: string | null;
	}>;
	estimated_total_hours?: number;
	similar_requests?: number;
	auto_approve_eligible?: boolean;
}

export interface SubmissionResult {
	request_id: string;
	task_id?: string;
	status: string;
	status_rollup?: string;
	estimated_completion?: string;
	approval_chain?: Array<{ step_number: number; approver_name: string }>;
	correlation_id?: string;
}

export interface UseAccessRequestFlowOptions {
	pluginId: string;
	pluginName: string;
	requestableRoles: string[];
	correlationId: string;
	deniedPath?: string;
}

export interface AccessRequestFlow {
	state: RequestFlowState;
	justification: string;
	setJustification: (value: string) => void;
	justificationValid: boolean;
	preview: ApprovalPreview | null;
	previewLoading: boolean;
	result: SubmissionResult | null;
	error: string | null;
	canSubmit: boolean;
	submit: () => Promise<void>;
	reset: () => void;
}

export const JUSTIFICATION_MIN = 10;
export const JUSTIFICATION_MAX = 2000;
const ACCESS_REQUEST_ENDPOINT = '/api/v1/access-requests';
const PREVIEW_ENDPOINT = '/api/v1/access-requests/preview';

export function useAccessRequestFlow(options: UseAccessRequestFlowOptions): AccessRequestFlow {
	const { pluginId, pluginName, requestableRoles, correlationId, deniedPath } = options;

	const [state, setState] = useState<RequestFlowState>('idle');
	const [justification, setJustificationRaw] = useState('');
	const [preview, setPreview] = useState<ApprovalPreview | null>(null);
	const [previewLoading, setPreviewLoading] = useState(false);
	const [result, setResult] = useState<SubmissionResult | null>(null);
	const [error, setError] = useState<string | null>(null);

	const justificationValid =
		justification.trim().length >= JUSTIFICATION_MIN &&
		justification.length <= JUSTIFICATION_MAX;

	const setJustification = useCallback((value: string) => {
		setJustificationRaw(value);
		setState(prev => {
			if (prev === 'idle' || prev === 'failed' || prev === 'auth_error') return 'composing';
			return prev;
		});
	}, []);

	useEffect(() => {
		if (requestableRoles.length === 0) return;

		let cancelled = false;
		setPreviewLoading(true);

		(async () => {
			try {
				const csrf = getCsrfToken();
				const res = await fetch(PREVIEW_ENDPOINT, {
					method: 'POST',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
						'X-CSRF-Token': csrf,
						'X-CSRFToken': csrf,
						'X-Requested-With': 'XMLHttpRequest',
					},
					body: JSON.stringify({
						app_id: pluginId,
						access_item_id: requestableRoles[0],
					}),
				});
				if (cancelled) return;
				if (res.ok) {
					const data = await res.json();
					setPreview(data);
				}
			} catch {
				// Preview is best-effort; silently skip on failure
			} finally {
				if (!cancelled) setPreviewLoading(false);
			}
		})();

		return () => { cancelled = true; };
	}, [pluginId, requestableRoles]);

	const canSubmit =
		justificationValid &&
		requestableRoles.length > 0 &&
		state !== 'submitting' &&
		state !== 'submitted';

	const submit = useCallback(async () => {
		if (!canSubmit) return;

		setState('submitting');
		setError(null);

		try {
			const csrf = getCsrfToken();
			const payload = {
				application_id: pluginId,
				application_name: pluginName,
				justification: justification.trim(),
				requested_roles: requestableRoles.map(role => ({
					role_id: role,
					role_name: role,
					justification: justification.trim(),
				})),
				source_system: 'empower',
				external_ref: deniedPath || `plugin:${pluginId}`,
				correlation_id: correlationId,
			};

			const response = await fetch(ACCESS_REQUEST_ENDPOINT, {
				method: 'POST',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
					'X-Correlation-ID': correlationId,
					'X-Request-ID': correlationId,
					'X-CSRF-Token': csrf,
					'X-CSRFToken': csrf,
					'X-Requested-With': 'XMLHttpRequest',
				},
				body: JSON.stringify(payload),
			});

			const data = await response.json().catch(() => ({}));

			if (response.ok) {
				setResult({
					request_id: data.request_id || 'submitted',
					task_id: data.task_id,
					status: data.status || 'submitted',
					status_rollup: data.status_rollup,
					estimated_completion: data.estimated_completion,
					approval_chain: data.approval_chain,
					correlation_id: data.correlation_id,
				});
				setState('submitted');
				return;
			}

			const detail = typeof data?.detail === 'string' ? data.detail : undefined;

			if (response.status === 409) {
				setError(detail || 'Eligibility or separation-of-duty conflict. Review the requested roles.');
				setState('sod_conflict');
				return;
			}

			if (response.status === 401 || response.status === 403) {
				setError(detail || 'Your session may have expired. Please sign in again, then retry.');
				setState('auth_error');
				return;
			}

			setError(detail || `Request failed (${response.status})`);
			setState('failed');
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to submit request');
			setState('failed');
		}
	}, [canSubmit, pluginId, pluginName, justification, requestableRoles, deniedPath, correlationId]);

	const reset = useCallback(() => {
		setState('idle');
		setError(null);
		setResult(null);
		setJustificationRaw('');
	}, []);

	return {
		state,
		justification,
		setJustification,
		justificationValid,
		preview,
		previewLoading,
		result,
		error,
		canSubmit,
		submit,
		reset,
	};
}
